import { Vec3 } from './Vec3';

/**
 * 4x4 column-major matrix backed by Float32Array.
 * Directly uploadable to WebGL uniforms.
 * Methods mutate in-place and return `this` for chaining.
 */
export class Mat4 {
  readonly data: Float32Array;

  constructor() {
    this.data = new Float32Array(16);
    this.identity();
  }

  identity(): this {
    this.data.fill(0);
    this.data[0] = 1; this.data[5] = 1; this.data[10] = 1; this.data[15] = 1;
    return this;
  }

  /** Perspective projection. fov in radians. */
  perspective(fov: number, aspect: number, near: number, far: number): this {
    const f = 1.0 / Math.tan(fov / 2);
    const rangeInv = 1.0 / (near - far);
    const d = this.data;
    d.fill(0);
    d[0] = f / aspect;
    d[5] = f;
    d[10] = (near + far) * rangeInv;
    d[11] = -1;
    d[14] = 2 * near * far * rangeInv;
    return this;
  }

  /** Look-at view matrix. */
  lookAt(eye: Vec3, target: Vec3, up: Vec3): this {
    const z = Vec3.SCRATCH_A.copy(eye).sub(target).normalize();
    const x = Vec3.SCRATCH_B.cross(up, z).normalize();
    const y = Vec3.SCRATCH_C.cross(z, x);

    const d = this.data;
    d[0] = x.x; d[1] = y.x; d[2]  = z.x; d[3]  = 0;
    d[4] = x.y; d[5] = y.y; d[6]  = z.y; d[7]  = 0;
    d[8] = x.z; d[9] = y.z; d[10] = z.z; d[11] = 0;
    d[12] = -x.dot(eye);
    d[13] = -y.dot(eye);
    d[14] = -z.dot(eye);
    d[15] = 1;
    return this;
  }

  /** Multiply: this = this * other */
  multiply(other: Mat4): this {
    const a = this.data;
    const b = other.data;
    const out = _SCRATCH_16;

    for (let col = 0; col < 4; col++) {
      for (let row = 0; row < 4; row++) {
        out[col * 4 + row] =
          a[row]      * b[col * 4] +
          a[4 + row]  * b[col * 4 + 1] +
          a[8 + row]  * b[col * 4 + 2] +
          a[12 + row] * b[col * 4 + 3];
      }
    }

    a.set(out);
    return this;
  }

  translate(x: number, y: number, z: number): this {
    const d = this.data;
    d[12] += d[0] * x + d[4] * y + d[8]  * z;
    d[13] += d[1] * x + d[5] * y + d[9]  * z;
    d[14] += d[2] * x + d[6] * y + d[10] * z;
    d[15] += d[3] * x + d[7] * y + d[11] * z;
    return this;
  }

  /** General 4x4 inversion via cofactor expansion. */
  invert(): this {
    const m = this.data;
    const t = _SCRATCH_16;
    t[0]  =  m[5]*m[10]*m[15] - m[5]*m[11]*m[14] - m[9]*m[6]*m[15] + m[9]*m[7]*m[14] + m[13]*m[6]*m[11] - m[13]*m[7]*m[10];
    t[4]  = -m[4]*m[10]*m[15] + m[4]*m[11]*m[14] + m[8]*m[6]*m[15] - m[8]*m[7]*m[14] - m[12]*m[6]*m[11] + m[12]*m[7]*m[10];
    t[8]  =  m[4]*m[9]*m[15]  - m[4]*m[11]*m[13] - m[8]*m[5]*m[15] + m[8]*m[7]*m[13] + m[12]*m[5]*m[11] - m[12]*m[7]*m[9];
    t[12] = -m[4]*m[9]*m[14]  + m[4]*m[10]*m[13] + m[8]*m[5]*m[14] - m[8]*m[6]*m[13] - m[12]*m[5]*m[10] + m[12]*m[6]*m[9];
    t[1]  = -m[1]*m[10]*m[15] + m[1]*m[11]*m[14] + m[9]*m[2]*m[15] - m[9]*m[3]*m[14] - m[13]*m[2]*m[11] + m[13]*m[3]*m[10];
    t[5]  =  m[0]*m[10]*m[15] - m[0]*m[11]*m[14] - m[8]*m[2]*m[15] + m[8]*m[3]*m[14] + m[12]*m[2]*m[11] - m[12]*m[3]*m[10];
    t[9]  = -m[0]*m[9]*m[15]  + m[0]*m[11]*m[13] + m[8]*m[1]*m[15] - m[8]*m[3]*m[13] - m[12]*m[1]*m[11] + m[12]*m[3]*m[9];
    t[13] =  m[0]*m[9]*m[14]  - m[0]*m[10]*m[13] - m[8]*m[1]*m[14] + m[8]*m[2]*m[13] + m[12]*m[1]*m[10] - m[12]*m[2]*m[9];
    t[2]  =  m[1]*m[6]*m[15]  - m[1]*m[7]*m[14]  - m[5]*m[2]*m[15] + m[5]*m[3]*m[14] + m[13]*m[2]*m[7]  - m[13]*m[3]*m[6];
    t[6]  = -m[0]*m[6]*m[15]  + m[0]*m[7]*m[14]  + m[4]*m[2]*m[15] - m[4]*m[3]*m[14] - m[12]*m[2]*m[7]  + m[12]*m[3]*m[6];
    t[10] =  m[0]*m[5]*m[15]  - m[0]*m[7]*m[13]  - m[4]*m[1]*m[15] + m[4]*m[3]*m[13] + m[12]*m[1]*m[7]  - m[12]*m[3]*m[5];
    t[14] = -m[0]*m[5]*m[14]  + m[0]*m[6]*m[13]  + m[4]*m[1]*m[14] - m[4]*m[2]*m[13] - m[12]*m[1]*m[6]  + m[12]*m[2]*m[5];
    t[3]  = -m[1]*m[6]*m[11]  + m[1]*m[7]*m[10]  + m[5]*m[2]*m[11] - m[5]*m[3]*m[10] - m[9]*m[2]*m[7]   + m[9]*m[3]*m[6];
    t[7]  =  m[0]*m[6]*m[11]  - m[0]*m[7]*m[10]  - m[4]*m[2]*m[11] + m[4]*m[3]*m[10] + m[8]*m[2]*m[7]   - m[8]*m[3]*m[6];
    t[11] = -m[0]*m[5]*m[11]  + m[0]*m[7]*m[9]   + m[4]*m[1]*m[11] - m[4]*m[3]*m[9]  - m[8]*m[1]*m[7]   + m[8]*m[3]*m[5];
    t[15] =  m[0]*m[5]*m[10]  - m[0]*m[6]*m[9]   - m[4]*m[1]*m[10] + m[4]*m[2]*m[9]  + m[8]*m[1]*m[6]   - m[8]*m[2]*m[5];

    const det = m[0]*t[0] + m[1]*t[4] + m[2]*t[8] + m[3]*t[12];
    if (Math.abs(det) < 1e-8) return this;
    const inv = 1.0 / det;
    for (let i = 0; i < 16; i++) m[i] = t[i] * inv;
    return this;
  }

  /** Pre-allocated for multiply. */
  static readonly SCRATCH = new Mat4();
}

const _SCRATCH_16 = new Float32Array(16);
